import PropTypes from 'prop-types';

import React from 'react';
import { Map } from 'immutable';
import classNames from 'classnames';
import { ContextMenu, MenuItem, ContextMenuTrigger } from 'react-contextmenu';

import uniqueId from '../../../../utils/uniqueId';
import { unwrap } from '../../../../utils/ChangeSpy';
import { PureInlineInput as BasePureInlineInput } from '../../../helpers/Input';
import { IconButton } from '../../../helpers/IconButton';
import PrettyError from '../../../decorators/PrettyError';
import DisplayError from '../../../decorators/DisplayError';
import { useAddNewIndustryContext } from '../../../../contexts/AddNewIndustryContext';

import style from './style.module.css';

const noErrors = Map();
const PureInlineInput = PrettyError(DisplayError(BasePureInlineInput));

const cellStyle = {
  height: '17px',
};

const ContextMenuWrapper = ({ onDelete, onEdit, children, data }) => {
  const id = uniqueId();

  return (
    <div>
      <ContextMenuTrigger collect={() => data.toJS()} id={id}>
        {children}
      </ContextMenuTrigger>

      <ContextMenu id={id}>
        <MenuItem onClick={onDelete}>
          <i className="fa fa-remove" /> Delete
        </MenuItem>
        <MenuItem onClick={onEdit}>
          <i className="fa fa-edit" /> Edit
        </MenuItem>
      </ContextMenu>
    </div>
  );
};

const percent = (target, base) => {
  const perc = (100 * target) / base;

  if (isNaN(perc) || !Number.isFinite(perc)) return null;

  return `${perc.toFixed(2)}%`;
};

const filter = (fiscalYear, year, field, revenue, key) => {
  const fieldValue = unwrap(fiscalYear.get(field));

  if (!fieldValue) return <td key={key} />;

  return (
    <td key={key}>
      <div>{percent(fieldValue, revenue)}</div>
    </td>
  );
};

const getGrowth = (revenue, prevReven, index) => {
  if (index === 0) return null;
  if (!revenue) return null;
  if (!prevReven) return null;

  const diff = revenue - prevReven;

  if (isNaN(diff)) return null;

  return percent(diff, prevReven);
};

const FiscalYearTable = props => {
  const { disabled, targetFiscalYears, onDelete, onAdd, onEdit, onChange } = props;
  const { onFiscalErrorClose: closeError } = useAddNewIndustryContext();

  const handleAddClick = (event, ...rest) => {
    if (!disabled) {
      return onAdd(event, ...rest);
    }
  };

  const handleDeleteClick = (event, ...rest) => {
    if (!disabled) {
      return onDelete(event, ...rest);
    }
  };

  const handleEditClick = (event, ...rest) => {
    if (!disabled) {
      return onEdit(event, ...rest);
    }
  };

  const cells = {
    revenue: [],
    gross: [],
    net: [],
    ebit: [],
    ebitda: [],
    capex: [],
  };

  const calculatedCells = {
    growth: [],
    grossPerc: [],
    ebitPerc: [],
    ebitdaPerc: [],
    netPerc: [],
  };

  const keys = Object.keys(cells);

  const columns = targetFiscalYears.map((fiscalYear, i, years) => {
    const year = unwrap(fiscalYear.get('fiscalYear'));
    const revenue = unwrap(fiscalYear.get('revenue'));
    const prevReven = unwrap(years.getIn([i - 1, 'revenue']));
    const inputErrors = fiscalYear.get('inputErrors', noErrors);
    const onErrorClose = (event, name) => closeError(event, name, i);
    const uniqueKey = `key_${i}`;

    keys.forEach(key => {
      cells[key].push(
        <td key={uniqueKey}>
          <PureInlineInput
            data-id={fiscalYear.get('id')}
            disabled={disabled}
            error={inputErrors.get(key, null)}
            name={key}
            onChange={event => onChange(event)}
            onErrorClose={onErrorClose}
            placeholder=""
            root={fiscalYear}
            type="currency"
            deep
          />
        </td>,
      );
    });

    calculatedCells.growth.push(<td key={`${uniqueKey}_growth`}>{getGrowth(revenue, prevReven, i)}</td>);
    calculatedCells.grossPerc.push(filter(fiscalYear, year, 'gross', revenue, `${uniqueKey}_grossPerc`));
    calculatedCells.ebitPerc.push(filter(fiscalYear, year, 'ebit', revenue, `${uniqueKey}_ebitPerc`));
    calculatedCells.ebitdaPerc.push(filter(fiscalYear, year, 'ebitda', revenue, `${uniqueKey}_ebitdaPerc`));
    calculatedCells.netPerc.push(filter(fiscalYear, year, 'net', revenue, `${uniqueKey}_netPerc`));

    return (
      <th key={uniqueKey}>
        <ContextMenuWrapper data={fiscalYear} onDelete={handleDeleteClick} onEdit={handleEditClick}>
          <div style={cellStyle}>{year}</div>
        </ContextMenuWrapper>
      </th>
    );
  });

  return (
    <form className="table-responsive text-left">
      <table className={classNames(style.table, 'table table-bordered table-hover table-striped')}>
        <thead>
          <tr>
            <th className="plus-icon">
              <IconButton
                disabled={disabled}
                icon="plus"
                onClick={handleAddClick}
                size="sm"
                title="Add fiscal year"
                variant="primary"
              />
            </th>
            {columns}
            <th />
          </tr>
        </thead>

        <tbody>
          <tr>
            <td>Revenue</td>
            {cells.revenue}
            <td />
          </tr>
          <tr>
            <td>Growth</td>
            {calculatedCells.growth}
            <td />
          </tr>

          <tr>
            <td>Gross</td>
            {cells.gross}
            <td />
          </tr>
          <tr>
            <td>%</td>
            {calculatedCells.grossPerc}
            <td />
          </tr>

          <tr>
            <td>EBIT</td>
            {cells.ebit}
            <td />
          </tr>
          <tr>
            <td>%</td>
            {calculatedCells.ebitPerc}
            <td />
          </tr>

          <tr>
            <td>EBITDA</td>
            {cells.ebitda}
            <td />
          </tr>
          <tr>
            <td>%</td>
            {calculatedCells.ebitdaPerc}
            <td />
          </tr>

          <tr>
            <td>Net Inc...</td>
            {cells.net}
            <td />
          </tr>
          <tr>
            <td>%</td>
            {calculatedCells.netPerc}
            <td />
          </tr>

          <tr>
            <td>CapEx</td>
            {cells.capex}
            <td />
          </tr>
        </tbody>
      </table>
    </form>
  );
};

FiscalYearTable.propTypes = {
  disabled: PropTypes.bool.isRequired,
};

export default FiscalYearTable;
